/**
 * db.ts —— IndexedDB 封装
 *
 * 设计文档 4.5 节的 Schema 落地。这一层只做三件事：
 *   1. 打开/升级数据库（建 store 与索引）
 *   2. 把回调式的 IDBRequest 包成 Promise
 *   3. 提供极简的 get / getAll / put / delete / query 原语
 *
 * 业务语义（排序规则、级联删除、事件广播）一律不放这里，放各 repo。
 * 这一层要足够薄，换存储引擎时只改这一个文件。
 */

const DB_NAME = 'lingxun';
const DB_VERSION = 1;

/** Object Store 名称，集中一处避免各处字符串写错 */
export const STORE = {
  contacts: 'contacts',
  sessions: 'sessions',
  messages: 'messages',
  files: 'files',
  fileDigests: 'file_digests',
  aiLog: 'ai_log',
} as const;

export type StoreName = (typeof STORE)[keyof typeof STORE];

let dbPromise: Promise<IDBDatabase> | null = null;

/** 打开数据库（首次调用时创建）。Concurrent 调用共享同一个 Promise */
export function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;

      // 联系人：按拼音、姓名建索引，支持排序与检索
      if (!db.objectStoreNames.contains(STORE.contacts)) {
        const s = db.createObjectStore(STORE.contacts, { keyPath: 'id' });
        s.createIndex('pinyin', 'pinyin');
        s.createIndex('name', 'name');
      }

      // 会话：按联系人、最后消息时间建索引
      if (!db.objectStoreNames.contains(STORE.sessions)) {
        const s = db.createObjectStore(STORE.sessions, { keyPath: 'id' });
        s.createIndex('contactId', 'contactId');
        s.createIndex('lastMessageAt', 'lastMessageAt');
      }

      // 消息：复合索引 [sessionId, createdAt] 用于分页游标读
      if (!db.objectStoreNames.contains(STORE.messages)) {
        const s = db.createObjectStore(STORE.messages, { keyPath: 'id' });
        s.createIndex('sessionId', 'sessionId');
        s.createIndex('sessionCreated', ['sessionId', 'createdAt']);
      }

      // 文件：按消息、会话建索引
      if (!db.objectStoreNames.contains(STORE.files)) {
        const s = db.createObjectStore(STORE.files, { keyPath: 'id' });
        s.createIndex('messageId', 'messageId');
        s.createIndex('sessionId', 'sessionId');
      }

      // 文件摘要索引（设计文档 5.1）
      if (!db.objectStoreNames.contains(STORE.fileDigests)) {
        const s = db.createObjectStore(STORE.fileDigests, { keyPath: 'id' });
        s.createIndex('sessionId', 'sessionId');
        s.createIndex('createdAt', 'createdAt');
      }

      // AI 调用日志（调试用）
      if (!db.objectStoreNames.contains(STORE.aiLog)) {
        const s = db.createObjectStore(STORE.aiLog, { keyPath: 'id' });
        s.createIndex('createdAt', 'createdAt');
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // 被浏览器强制关闭（如存储配额清理）时让下次调用重新打开
    req.onblocked = () => {
      dbPromise = null;
      reject(new Error('数据库被其他标签页占用，请关闭其他标签页后重试'));
    };
  });

  return dbPromise;
}

/** 把 IDBRequest 包成 Promise */
export function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** 在一个事务里执行操作，事务完成后 resolve */
export async function withStore<T>(
  storeName: StoreName,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => T,
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result: T;

    try {
      result = fn(store);
    } catch (err) {
      reject(err);
      return;
    }

    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('事务被中止'));
  });
}

/* ============================================================
   基础原语
   ============================================================ */

export async function dbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB();
  const tx = db.transaction(store, 'readonly');
  return promisify<T | undefined>(tx.objectStore(store).get(key));
}

export async function dbGetAll<T>(store: StoreName): Promise<T[]> {
  const db = await openDB();
  const tx = db.transaction(store, 'readonly');
  return promisify<T[]>(tx.objectStore(store).getAll());
}

export async function dbPut(store: StoreName, value: unknown): Promise<void> {
  await withStore(store, 'readwrite', (s) => s.put(value));
}

/** 批量写入，同一个事务内完成 —— 比逐条 put 快得多 */
export async function dbPutMany(store: StoreName, values: unknown[]): Promise<void> {
  if (values.length === 0) return;
  await withStore(store, 'readwrite', (s) => {
    values.forEach((v) => s.put(v));
  });
}

export async function dbDelete(store: StoreName, key: IDBValidKey): Promise<void> {
  await withStore(store, 'readwrite', (s) => s.delete(key));
}

/**
 * 按索引查询。prefix 用于复合索引的前缀匹配（如按 sessionId 查某会话的消息）。
 */
export async function dbQueryByIndex<T>(
  store: StoreName,
  indexName: string,
  query: IDBValidKey | IDBKeyRange,
): Promise<T[]> {
  const db = await openDB();
  const tx = db.transaction(store, 'readonly');
  const idx = tx.objectStore(store).index(indexName);
  return promisify<T[]>(idx.getAll(query));
}

/**
 * 按索引倒序取前 N 条 —— 会话列表、消息分页都靠它。
 * 倒序游标 + 计数提前终止，避免把整表读进内存。
 */
export async function dbQueryByIndexDesc<T>(
  store: StoreName,
  indexName: string,
  limit: number,
  range?: IDBKeyRange,
): Promise<T[]> {
  if (limit <= 0) return [];
  const db = await openDB();
  const tx = db.transaction(store, 'readonly');
  const idx = tx.objectStore(store).index(indexName);

  return new Promise<T[]>((resolve, reject) => {
    const out: T[] = [];
    const req = idx.openCursor(range, 'prev');
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor || out.length >= limit) {
        resolve(out);
        return;
      }
      out.push(cursor.value as T);
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

/** 统计某 store 的记录数 */
export async function dbCount(store: StoreName): Promise<number> {
  const db = await openDB();
  const tx = db.transaction(store, 'readonly');
  return promisify<number>(tx.objectStore(store).count());
}

/** 清空所有业务数据（设置页的「清空全部数据」）。
 *  六个 store 全部清掉（含 ai_log）—— 这是一次真实的删除，不是 mock。
 *  「全部」就是全部：调试日志也是本地数据，跟着一起走，
 *  否则用户以为清干净了，实际上还有一份对话记录躺在库里。 */
export async function dbClearAll(): Promise<void> {
  const db = await openDB();
  const stores: StoreName[] = [
    STORE.contacts,
    STORE.sessions,
    STORE.messages,
    STORE.files,
    STORE.fileDigests,
    STORE.aiLog,
  ];
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(stores, 'readwrite');
    stores.forEach((name) => tx.objectStore(name).clear());
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** 生成 uuid。优先用原生实现（需要安全上下文），降级到随机串 */
export function uid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
